import { useEffect, useState, useCallback } from "react";
import adminApi from "@/lib/adminApi";
import {
  PageHeader, AdminCard, StatCard, DataTable, StatusPill, FilterChips, PageLoader, datetimeFromIso,
} from "@/admin/components";
import { formatINR } from "@/lib/format";

const PAYMENT_FILTERS = ["All", "paid", "pending", "failed"];
const FULFILMENT_FILTERS = ["All", "pending", "email_sent", "shipped", "delivered"];

export default function ProductOrders() {
  const [orders, setOrders] = useState(null);
  const [stats, setStats] = useState(null);
  const [payFilter, setPayFilter] = useState("All");
  const [fulFilter, setFulFilter] = useState("All");

  const load = useCallback(async () => {
    const params = {};
    if (payFilter !== "All") params.payment_status = payFilter;
    if (fulFilter !== "All") params.fulfilment_status = fulFilter;
    const [ordersRes, statsRes] = await Promise.all([
      adminApi.get("/admin/product-orders", { params }),
      adminApi.get("/admin/product-orders/stats"),
    ]);
    setOrders(ordersRes.data.orders);
    setStats(statsRes.data);
  }, [payFilter, fulFilter]);

  useEffect(() => { load(); }, [load]);

  const columns = [
    {
      key: "order_reference", label: "Reference",
      render: (r) => (
        <span className="font-mono text-[12px] font-semibold text-[#111]" data-testid={`order-ref-${r.id}`}>
          #{r.order_reference}
        </span>
      ),
    },
    {
      key: "product_name", label: "Product",
      render: (r) => <span className="font-medium text-[#111]">{r.product_name}</span>,
    },
    {
      key: "astrologer_name", label: "Astrologer",
      render: (r) => <span className="text-[#555]">{r.astrologer_name || "—"}</span>,
    },
    {
      key: "client_name", label: "Client",
      render: (r) => (
        <div>
          <div className="font-medium text-[#111]">{r.client_name}</div>
          <div className="text-[11px] text-[#888]">{r.client_email}</div>
        </div>
      ),
    },
    { key: "selling_price_inr", label: "Price", render: (r) => formatINR(r.selling_price_inr) },
    { key: "astrologer_commission_inr", label: "Commission", render: (r) => formatINR(r.astrologer_commission_inr) },
    {
      key: "payment_status", label: "Payment",
      render: (r) => {
        const tone = r.payment_status === "paid" ? "green" : r.payment_status === "failed" ? "red" : "amber";
        return <StatusPill status={r.payment_status} tone={tone} />;
      },
    },
    {
      key: "fulfilment_status", label: "Fulfilment",
      render: (r) => {
        const tone = r.fulfilment_status === "delivered" ? "dark"
          : r.fulfilment_status === "shipped" ? "green"
          : r.fulfilment_status === "email_sent" ? "amber"
          : "gray";
        const label = r.fulfilment_status === "email_sent" ? "Email Sent"
          : r.fulfilment_status === "shipped" ? "Shipped"
          : r.fulfilment_status === "delivered" ? "Delivered"
          : "Pending";
        return <StatusPill status={r.fulfilment_status} tone={tone} label={label} />;
      },
    },
    {
      key: "created_at", label: "Date",
      render: (r) => <span className="text-[12px] text-[#888]">{datetimeFromIso(r.created_at)}</span>,
    },
  ];

  return (
    <div>
      <PageHeader title="Product Orders" subtitle="Orders placed via astrologer profiles" />

      {stats && (
        <div className="mb-6 grid grid-cols-2 gap-4 lg:grid-cols-4">
          <StatCard label="Total Orders" value={stats.total_orders} testid="stat-total-orders" />
          <StatCard label="Total Revenue" value={formatINR(stats.total_revenue_inr)} testid="stat-revenue" />
          <StatCard label="Commission Owed" value={formatINR(stats.total_commission_owed_inr)} testid="stat-commission-owed" />
          <StatCard label="Pending Commission" value={formatINR(stats.pending_commission_inr)} testid="stat-pending-commission" />
        </div>
      )}

      <AdminCard className="mb-4 p-4 space-y-3">
        <div>
          <p className="mb-1.5 text-[11px] font-semibold uppercase tracking-wide text-[#999]">Payment status</p>
          <FilterChips
            options={PAYMENT_FILTERS}
            value={payFilter}
            onChange={setPayFilter}
            testidPrefix="pay-filter"
          />
        </div>
        <div>
          <p className="mb-1.5 text-[11px] font-semibold uppercase tracking-wide text-[#999]">Fulfilment status</p>
          <FilterChips
            options={FULFILMENT_FILTERS}
            value={fulFilter}
            onChange={setFulFilter}
            testidPrefix="ful-filter"
          />
        </div>
      </AdminCard>

      {orders === null ? <PageLoader /> : (
        <AdminCard>
          <DataTable columns={columns} rows={orders} empty="No orders found" testid="product-orders-table" />
        </AdminCard>
      )}
    </div>
  );
}
